import { useMemo, useState } from "react";
import CalculatorLayout from "@/components/CalculatorLayout";
import { ResultCard } from "@/components/ResultCard";
import AgeGenderFields from "@/components/AgeGenderFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { round, parseNum, ftInToCm, lbToKg } from "@/calculators/_helpers";

// Shared: gather age/gender/height/weight/activity for Mifflin-St Jeor calcs.
const ACTIVITY = [
  { id: "sedentary", label: "Sedentary (little/no exercise)", mult: 1.2 },
  { id: "light", label: "Light (1–3 days/week)", mult: 1.375 },
  { id: "moderate", label: "Moderate (3–5 days/week)", mult: 1.55 },
  { id: "active", label: "Active (6–7 days/week)", mult: 1.725 },
  { id: "very", label: "Very active (2x/day)", mult: 1.9 },
];

function ActivitySelect({ value, onChange }) {
  return (
    <div className="space-y-2">
      <Label>Activity level</Label>
      <select
        data-testid="activity-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
      >
        {ACTIVITY.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
      </select>
    </div>
  );
}

function useMifflinInputs() {
  const [unit, setUnit] = useState("metric");
  const [gender, setGender] = useState("male");
  const [age, setAge] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [ft, setFt] = useState("");
  const [inches, setInches] = useState("");
  const [weight, setWeight] = useState("");

  const height_cm = unit === "metric" ? parseNum(heightCm) : ftInToCm(ft, inches);
  const weight_kg = unit === "metric" ? parseNum(weight) : lbToKg(parseNum(weight));
  const ageN = parseNum(age);

  const bmr = ageN > 0 && height_cm > 0 && weight_kg > 0
    ? gender === "male"
      ? 10 * weight_kg + 6.25 * height_cm - 5 * ageN + 5
      : 10 * weight_kg + 6.25 * height_cm - 5 * ageN - 161
    : 0;

  const InputsUI = (
    <div className="space-y-4">
      <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
      <RadioGroup value={unit} onValueChange={setUnit} className="flex gap-2" data-testid="unit-toggle">
        {["metric", "imperial"].map((u) => (
          <div key={u} className="flex-1">
            <RadioGroupItem value={u} id={`u-${u}`} className="peer sr-only" />
            <Label htmlFor={`u-${u}`} className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary">
              {u === "metric" ? "Metric" : "Imperial"}
            </Label>
          </div>
        ))}
      </RadioGroup>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>{unit === "metric" ? "Weight (kg)" : "Weight (lb)"}</Label>
          <Input type="number" inputMode="decimal" data-testid="in-weight" value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
        <div className="space-y-2"><Label>Height</Label>
          {unit === "metric"
            ? <Input type="number" inputMode="decimal" data-testid="in-height-cm" placeholder="cm" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
            : <div className="grid grid-cols-2 gap-2">
                <Input type="number" data-testid="in-height-ft" placeholder="ft" value={ft} onChange={(e) => setFt(e.target.value)} />
                <Input type="number" data-testid="in-height-in" placeholder="in" value={inches} onChange={(e) => setInches(e.target.value)} />
              </div>}
        </div>
      </div>
    </div>
  );

  return { InputsUI, bmr, weight_kg, height_cm, ageN, gender, unit };
}

// ---------- Calorie ----------
export function CalorieCalculator({ calc }) {
  const [activity, setActivity] = useState("moderate");
  const [goal, setGoal] = useState("maintain");
  const { InputsUI, bmr } = useMifflinInputs();
  const mult = ACTIVITY.find((a) => a.id === activity)?.mult || 1.55;
  const tdee = bmr * mult;
  const adjust = { extremeLoss: -1000, loss: -500, mildLoss: -250, maintain: 0, mildGain: 250, gain: 500 }[goal] || 0;
  const target = tdee > 0 ? tdee + adjust : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Daily calorie target to lose, maintain or gain weight, using Mifflin-St Jeor + activity multiplier."
      resultText={target > 0 ? `Daily calorie target: ${Math.round(target)} kcal (BMR ${Math.round(bmr)} · TDEE ${Math.round(tdee)}).` : null}
      formula={<><p>BMR (Mifflin-St Jeor) × activity factor = TDEE. Weight loss/gain applies ±250–1000 kcal per day (about 0.25–1 kg / week).</p></>}
      faqs={[
        { q: "Is a 500 kcal deficit safe?", a: "For most healthy adults, yes — it targets ~0.5 kg/week. Very low targets (<1200 kcal for women, <1500 for men) usually need supervision." },
        { q: "Should I track every day?", a: "Track for a week or two to learn portions, then adjust based on weekly weigh-ins rather than daily obsession." },
      ]}
    >
      {InputsUI}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <ActivitySelect value={activity} onChange={setActivity} />
        <div className="space-y-2">
          <Label>Goal</Label>
          <select data-testid="goal-select" value={goal} onChange={(e) => setGoal(e.target.value)} className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
            <option value="extremeLoss">Extreme weight loss (-1000 kcal)</option>
            <option value="loss">Weight loss (-500 kcal)</option>
            <option value="mildLoss">Mild weight loss (-250 kcal)</option>
            <option value="maintain">Maintain</option>
            <option value="mildGain">Mild weight gain (+250 kcal)</option>
            <option value="gain">Weight gain (+500 kcal)</option>
          </select>
        </div>
      </div>
      {target > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <ResultCard label="Daily target" value={Math.round(target)} unit="kcal" testId="cal-target" />
          <div className="rounded-2xl border border-border p-6"><div className="text-xs uppercase text-muted-foreground">BMR</div><div className="mt-2 text-2xl font-bold">{Math.round(bmr)} kcal</div></div>
          <div className="rounded-2xl border border-border p-6"><div className="text-xs uppercase text-muted-foreground">TDEE</div><div className="mt-2 text-2xl font-bold">{Math.round(tdee)} kcal</div></div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- BMR ----------
export function BmrCalculator({ calc }) {
  const { InputsUI, bmr } = useMifflinInputs();
  return (
    <CalculatorLayout
      calc={calc}
      intro="Basal Metabolic Rate — calories your body burns at complete rest."
      resultText={bmr > 0 ? `BMR: ${Math.round(bmr)} kcal/day.` : null}
      formula={<>
        <p><strong>Men:</strong> BMR = 10 × weight + 6.25 × height − 5 × age + 5</p>
        <p><strong>Women:</strong> BMR = 10 × weight + 6.25 × height − 5 × age − 161</p>
        <p>Weight in kg, height in cm. This is the Mifflin-St Jeor equation, considered the most accurate for the general population.</p>
      </>}
      faqs={[
        { q: "What's the difference between BMR and TDEE?", a: "BMR is at-rest calories. TDEE = BMR × your activity level — total daily calories burned." },
        { q: "Does BMR drop with age?", a: "Yes — mainly because muscle mass declines. Strength training offsets a lot of the drop." },
      ]}
    >
      {InputsUI}
      {bmr > 0 && <div className="mt-6"><ResultCard label="BMR" value={Math.round(bmr)} unit="kcal / day" testId="bmr-result" /></div>}
    </CalculatorLayout>
  );
}

// ---------- TDEE ----------
export function TdeeCalculator({ calc }) {
  const [activity, setActivity] = useState("moderate");
  const { InputsUI, bmr } = useMifflinInputs();
  const mult = ACTIVITY.find((a) => a.id === activity)?.mult || 1.55;
  const tdee = bmr * mult;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Total Daily Energy Expenditure — calories burned per day including activity."
      resultText={tdee > 0 ? `TDEE: ${Math.round(tdee)} kcal/day.` : null}
      formula={<p>TDEE = BMR (Mifflin-St Jeor) × activity multiplier (1.2 sedentary → 1.9 very active).</p>}
      faqs={[
        { q: "Which activity level should I choose?", a: "Be honest — most people over-estimate. If you sit at a desk and exercise 3× a week, that's 'light' to 'moderate'." },
        { q: "Is TDEE the same every day?", a: "No, it varies with steps, workouts and diet-induced thermogenesis. Treat it as a weekly average." },
      ]}
    >
      {InputsUI}
      <div className="mt-4"><ActivitySelect value={activity} onChange={setActivity} /></div>
      {tdee > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <ResultCard label="TDEE" value={Math.round(tdee)} unit="kcal / day" testId="tdee-result" />
          <div className="rounded-2xl border border-border p-6"><div className="text-xs uppercase text-muted-foreground">BMR</div><div className="mt-2 text-2xl font-bold">{Math.round(bmr)} kcal</div></div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Macro ----------
const DIETS = [
  { id: "balanced", label: "Balanced (30P / 40C / 30F)", p: 0.30, c: 0.40, f: 0.30 },
  { id: "lowcarb", label: "Low carb (40P / 20C / 40F)", p: 0.40, c: 0.20, f: 0.40 },
  { id: "highprotein", label: "High protein (40P / 30C / 30F)", p: 0.40, c: 0.30, f: 0.30 },
  { id: "keto", label: "Keto (25P / 5C / 70F)", p: 0.25, c: 0.05, f: 0.70 },
  { id: "endurance", label: "Endurance (20P / 60C / 20F)", p: 0.20, c: 0.60, f: 0.20 },
];

export function MacroCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [cal, setCal] = useState("");
  const [diet, setDiet] = useState("balanced");
  const d = DIETS.find((x) => x.id === diet);
  const calN = parseNum(cal);
  const p = calN > 0 ? (calN * d.p) / 4 : 0;
  const c = calN > 0 ? (calN * d.c) / 4 : 0;
  const f = calN > 0 ? (calN * d.f) / 9 : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Split your daily calories into protein, carbs and fat."
      resultText={p > 0 ? `${d.label}: ${round(p)}g P · ${round(c)}g C · ${round(f)}g F.` : null}
      formula={<p>Protein g = calories × %P ÷ 4. Carbs g = calories × %C ÷ 4. Fat g = calories × %F ÷ 9.</p>}
      faqs={[
        { q: "Which split is best for fat loss?", a: "Higher protein (30–40%) helps preserve muscle and satiety in a deficit." },
        { q: "Is keto safe long-term?", a: "For most people it's safe short-term. Long-term evidence is mixed — discuss with a clinician if you have conditions." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Daily calories</Label>
            <Input type="number" inputMode="numeric" data-testid="in-cal" value={cal} onChange={(e) => setCal(e.target.value)} placeholder="e.g. 2200" /></div>
          <div className="space-y-2"><Label>Diet style</Label>
            <select data-testid="diet-select" value={diet} onChange={(e) => setDiet(e.target.value)} className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
              {DIETS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select></div>
        </div>
      </div>
      {p > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="macro-result">
          <ResultCard label="Protein" value={round(p)} unit="g" tone="good" />
          <ResultCard label="Carbs" value={round(c)} unit="g" tone="warning" />
          <ResultCard label="Fat" value={round(f)} unit="g" />
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Protein ----------
const PROTEIN_LVLS = [
  { id: "sedentary", label: "Sedentary — 0.8 g/kg", g: 0.8 },
  { id: "light", label: "Lightly active — 1.2 g/kg", g: 1.2 },
  { id: "moderate", label: "Moderate — 1.5 g/kg", g: 1.5 },
  { id: "active", label: "Active / gym-goer — 1.8 g/kg", g: 1.8 },
  { id: "athlete", label: "Athlete / cutting — 2.2 g/kg", g: 2.2 },
];
export function ProteinCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [unit, setUnit] = useState("metric");
  const [w, setW] = useState("");
  const [lvl, setLvl] = useState("moderate");
  const kg = unit === "metric" ? parseNum(w) : lbToKg(parseNum(w));
  const rate = PROTEIN_LVLS.find((x) => x.id === lvl).g;
  const target = kg * rate;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Daily protein grams based on your body weight and training level."
      resultText={target > 0 ? `Protein target: ${round(target)} g/day.` : null}
      formula={<p>Grams = body weight (kg) × factor (0.8 sedentary → 2.2 athlete). International guidelines put general adults at 0.8 g/kg; trainees typically benefit from 1.6–2.2 g/kg.</p>}
      faqs={[
        { q: "Can too much protein damage kidneys?", a: "For healthy adults, no strong evidence supports that concern. People with existing kidney disease should consult their clinician." },
        { q: "Should I hit protein daily?", a: "Total daily intake matters most; spreading it into 3–5 meals of 20–40 g each is optimal for muscle protein synthesis." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Unit</Label>
            <RadioGroup value={unit} onValueChange={setUnit} className="flex gap-2">
              {["metric", "imperial"].map((u) => (
                <div key={u} className="flex-1">
                  <RadioGroupItem value={u} id={`pu-${u}`} className="peer sr-only" />
                  <Label htmlFor={`pu-${u}`} className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary">{u === "metric" ? "kg" : "lb"}</Label>
                </div>
              ))}
            </RadioGroup></div>
          <div className="space-y-2"><Label>Weight ({unit === "metric" ? "kg" : "lb"})</Label>
            <Input type="number" inputMode="decimal" data-testid="in-weight" value={w} onChange={(e) => setW(e.target.value)} /></div>
        </div>
        <div className="space-y-2">
          <Label>Activity / goal</Label>
          <select data-testid="protein-level" value={lvl} onChange={(e) => setLvl(e.target.value)} className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
            {PROTEIN_LVLS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </div>
      </div>
      {target > 0 && <div className="mt-6"><ResultCard label="Daily protein" value={round(target)} unit="g" tone="good" testId="protein-result" /></div>}
    </CalculatorLayout>
  );
}

// ---------- Fat Intake ----------
export function FatIntakeCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [cal, setCal] = useState("");
  const calN = parseNum(cal);
  const min = calN > 0 ? (calN * 0.20) / 9 : 0;
  const max = calN > 0 ? (calN * 0.35) / 9 : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Daily fat range from your calorie target (20–35% of calories)."
      resultText={min > 0 ? `Fat: ${round(min)}–${round(max)} g/day.` : null}
      formula={<p>Fat grams = calories × 20–35% ÷ 9 kcal per gram. Prioritise unsaturated fats (olive oil, nuts, fish) and limit trans fats.</p>}
      faqs={[
        { q: "Is low-fat better than low-carb?", a: "Neither is universally better. Adherence and food quality dominate outcomes." },
        { q: "Where should my fat calories come from?", a: "Mostly mono- and poly-unsaturated fats: olive oil, avocados, nuts, seeds, fatty fish." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="space-y-2"><Label>Daily calories</Label>
          <Input type="number" inputMode="numeric" data-testid="in-cal" value={cal} onChange={(e) => setCal(e.target.value)} placeholder="e.g. 2200" /></div>
      </div>
      {min > 0 && <div className="mt-6"><ResultCard label="Daily fat" value={`${round(min)}–${round(max)}`} unit="g" testId="fat-result" /></div>}
    </CalculatorLayout>
  );
}

// ---------- Carbohydrate ----------
export function CarbohydrateCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [cal, setCal] = useState("");
  const calN = parseNum(cal);
  const min = calN > 0 ? (calN * 0.45) / 4 : 0;
  const max = calN > 0 ? (calN * 0.65) / 4 : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Daily carbohydrate range from your calorie target (45–65% of calories)."
      resultText={min > 0 ? `Carbs: ${round(min)}–${round(max)} g/day.` : null}
      formula={<p>Carb grams = calories × 45–65% ÷ 4 kcal per gram. Endurance athletes trend toward the top of the range; low-carb dieters below.</p>}
      faqs={[
        { q: "Are carbs bad?", a: "Not inherently. Whole-food carbs (oats, rice, potatoes, fruit, veg) are the primary fuel for most trainees." },
        { q: "How many carbs on keto?", a: "Typically under 50 g/day, and often under 20 g for strict keto." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="space-y-2"><Label>Daily calories</Label>
          <Input type="number" inputMode="numeric" data-testid="in-cal" value={cal} onChange={(e) => setCal(e.target.value)} placeholder="e.g. 2200" /></div>
      </div>
      {min > 0 && <div className="mt-6"><ResultCard label="Daily carbs" value={`${round(min)}–${round(max)}`} unit="g" tone="warning" testId="carb-result" /></div>}
    </CalculatorLayout>
  );
}

// ---------- Weight Watcher Points ----------
export function WeightWatcherPointsCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [calv, setCal] = useState("");
  const [protein, setProtein] = useState("");
  const [satfat, setSatfat] = useState("");
  const [sugar, setSugar] = useState("");
  const c = parseNum(calv), p = parseNum(protein), s = parseNum(satfat), sg = parseNum(sugar);
  const raw = c > 0 ? (c * 0.0305) + (s * 0.275) - (p * 0.098) + (sg * 0.12) : 0;
  const points = c > 0 ? Math.max(0, Math.round(raw)) : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimated Weight-Watchers-style SmartPoints per food or meal."
      resultText={c > 0 ? `Estimated points: ${points}.` : null}
      formula={<p>Points ≈ (kcal × 0.0305) + (sat fat g × 0.275) − (protein g × 0.098) + (sugar g × 0.12). This is a public-domain approximation, not the official Weight Watchers formula.</p>}
      faqs={[
        { q: "Is this the official WW formula?", a: "No — WW's formula is proprietary. This is a widely-used approximation." },
        { q: "Why does protein subtract points?", a: "Protein has high satiety and thermic effect, so it's rewarded in WW's system." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Calories</Label><Input type="number" data-testid="ww-cal" value={calv} onChange={(e) => setCal(e.target.value)} /></div>
          <div className="space-y-2"><Label>Protein (g)</Label><Input type="number" data-testid="ww-p" value={protein} onChange={(e) => setProtein(e.target.value)} /></div>
          <div className="space-y-2"><Label>Saturated fat (g)</Label><Input type="number" data-testid="ww-s" value={satfat} onChange={(e) => setSatfat(e.target.value)} /></div>
          <div className="space-y-2"><Label>Sugar (g)</Label><Input type="number" data-testid="ww-sg" value={sugar} onChange={(e) => setSugar(e.target.value)} /></div>
        </div>
      </div>
      {c > 0 && <div className="mt-6"><ResultCard label="Estimated points" value={points} testId="ww-result" /></div>}
    </CalculatorLayout>
  );
}
